import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, AlertTriangle, ChevronRight } from 'lucide-react';
import { Button } from '../../ui/button';
import MobilitySilhouette, { colorForScore } from '../../MobilitySilhouette';

/**
 * MobilityMapCard — inline chat card showing the user's current Mobility &
 * ROM map. Rendered when the user asks Nikki "show me my mobility" (or
 * similar) and a baseline already exists.
 *
 * Compact: silhouette + overall score + top asymmetry + focus chips,
 * with a CTA into /mobility for the deep dive.
 *
 * Data shape (sent from chat.py _intent_show_assessment when baseline is
 * present):
 *   {
 *     overall_mobility: int,
 *     region_scores: { ... },
 *     asymmetry_flags: [ ... ],
 *     weakest_regions: [ ... ],
 *     taken_at: ISO,
 *     needs_retest: bool,
 *   }
 */
const MobilityMapCard = ({ data = {} }) => {
  const navigate = useNavigate();
  const {
    overall_mobility = 0,
    region_scores = {},
    asymmetry_flags = [],
    weakest_regions = [],
    taken_at,
    needs_retest = false,
  } = data;

  return (
    <div className="hpme-card p-4 relative" data-testid="mobility-map-card">
      <div className="hpme-card-corner" />

      <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
        <div>
          <p className="hpme-label flex items-center gap-1.5">
            <Activity className="w-3 h-3 text-primary" /> MOBILITY &amp; ROM
          </p>
          <p className="font-secondary font-bold text-2xl tracking-tight leading-none mt-1">
            <span style={{ color: colorForScore(overall_mobility) }}>{overall_mobility}</span>
            <span className="text-zinc-500 text-sm ml-1">/ 100</span>
          </p>
          {taken_at && (
            <p className="text-[10px] text-zinc-600 mt-1">Mapped {new Date(taken_at).toLocaleDateString()}</p>
          )}
        </div>
        {needs_retest && (
          <span className="text-[10px] uppercase font-secondary font-bold px-2 py-1 rounded-sm bg-yellow-500/20 text-yellow-300 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> Retest due
          </span>
        )}
      </div>

      <div className="grid grid-cols-[180px_minmax(0,1fr)] gap-4 items-start">
        <div className="flex justify-center">
          <div className="w-full max-w-[170px] h-[180px] pointer-events-none">
            <MobilitySilhouette
              regionScores={region_scores}
              asymmetryFlags={asymmetry_flags}
              size="sm"
              showBack
              interactive={false}
            />
          </div>
        </div>

        <div className="space-y-2.5 min-w-0">
          {asymmetry_flags.length > 0 && (
            <div>
              <p className="text-[9px] text-zinc-500 uppercase tracking-wider mb-1">
                ASYMMETRY · top {Math.min(2, asymmetry_flags.length)}
              </p>
              <div className="space-y-1">
                {asymmetry_flags.slice(0, 2).map(f => (
                  <div key={f.test_id} className="flex items-center gap-2 text-[11px] py-1 px-2 rounded-sm bg-amber-500/10 border border-amber-500/30">
                    <span className="text-amber-200/90">{f.test_name}</span>
                    <span className="ml-auto font-secondary font-bold text-amber-200 whitespace-nowrap">
                      L {f.left_score}<span className="text-amber-500/60 mx-1">·</span>R {f.right_score}
                      <span className="ml-1 text-amber-400">Δ{f.delta}</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {weakest_regions.length > 0 && (
            <div>
              <p className="text-[9px] text-zinc-500 uppercase tracking-wider mb-1">FOCUS · driving your plan</p>
              <div className="flex gap-1 flex-wrap">
                {weakest_regions.slice(0, 4).map(k => (
                  <span key={k} className="px-2 py-1 rounded-sm bg-yellow-500/15 text-yellow-300 text-[10px] uppercase font-semibold whitespace-nowrap">
                    {k.replace('_L', ' L').replace('_R', ' R').replace('_', ' ')}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="mt-3 flex gap-2 flex-wrap">
        <Button
          className="hpme-btn-primary h-8 text-xs"
          onClick={() => navigate('/mobility')}
          data-testid="mobility-map-open-btn"
        >
          Open full map <ChevronRight className="w-3 h-3 ml-1" />
        </Button>
        <Button
          variant="outline"
          className="h-8 text-xs"
          onClick={() => navigate('/assessment')}
          data-testid="mobility-map-retest-btn"
        >
          Retest mobility
        </Button>
      </div>
    </div>
  );
};

export default MobilityMapCard;
